import { useState } from "react";
import { toast } from "react-toastify";
import styled from "styled-components";
import { queryClient } from "../../..";
import {
  DeliveryQueryKey,
  useGetDeliveryRecentLocationPageQuery,
} from "../../../query/delivery-query";
import {
  DeliveryDto,
  RecentLocationContentByDeliveryDto,
} from "../../../types/delivery/delivery-dto";
import { DeliveryGroupType } from "../../../types/schedule/delivery-group-type";
import { convertDeliveryDtoToDelivery } from "../../../utils/converter/delivery/delivery-converter";
import { useDebouncedCallback } from "../../../utils/custom-hook/use-debounced-callback";
import useDidMountEffect from "../../../utils/custom-hook/use-did-mount-effect";
import { OutlinedBlackButton } from "../../button/OutlinedBlackButton";
import { GeneralTextField } from "../../text-field/GeneralTextField";
import { WorkPlaceForm } from "./DeliveryDialog";
import { RecentLocationSearchItemList } from "./RecentLocationSearchItemList";
import { SelectRecentLocationCard } from "./SelectedRecentLocationCard";

interface Props {
  clientId: string;
  addressForm: string;
  workPlaceForm: WorkPlaceForm;
  deliveryPayloadValidatorAddress: boolean;
  deliveryPayloadValidatorDetail: boolean;
  type: DeliveryGroupType;
  setAddressForm: React.Dispatch<React.SetStateAction<string>>;
  setWorkPlaceForm: React.Dispatch<React.SetStateAction<WorkPlaceForm>>;
}

export const RecentLocationSearchCard: React.FC<Props> = ({
  clientId,
  addressForm,
  workPlaceForm,
  deliveryPayloadValidatorAddress,
  deliveryPayloadValidatorDetail,
  type,
  setAddressForm,
  setWorkPlaceForm,
}) => {
  const [payload, setPayload] = useState<any>({});
  const [searchKeyword, setSearchKeyword] = useState<string>("");
  const [open, setOpen] = useState(false);
  const { data: recentLocationByDelivery } =
    useGetDeliveryRecentLocationPageQuery(payload);

  const getRecentLocations = () => {
    if (!recentLocationByDelivery) {
      return [];
    }

    if (!searchKeyword) {
      return [];
    }

    const recentLocations: RecentLocationContentByDeliveryDto[] =
      recentLocationByDelivery.data.contents.map((deliveryDto: DeliveryDto) => {
        const delivery = convertDeliveryDtoToDelivery(deliveryDto);
        if (type === DeliveryGroupType.PickUp) {
          return {
            workPlace: delivery.pickUpWorkPlace,
            roadAddress: delivery.pickUpLocation.road,
            jibunAddress: delivery.pickUpLocation.jibun,
            dateToWork: delivery.dateToWork,
          };
        }
        return {
          workPlace: delivery.dropOffWorkPlace,
          roadAddress: delivery.dropOffLocation.road,
          jibunAddress: delivery.dropOffLocation.jibun,
          dateToWork: delivery.dateToWork,
        };
      });
    return recentLocations;
  };

  const recentLocations = getRecentLocations();

  const setSearchPayload = () => {
    queryClient.invalidateQueries(DeliveryQueryKey.RecentLocationPage);
    const newPayload: any = {
      clientId,
    };
    if (searchKeyword !== "") {
      newPayload["keyword"] = searchKeyword;
    }
    const params = {
      payload: newPayload,
      type,
    };
    setPayload(params);
  };

  const handleClickLocationButton = () => {
    setOpen(!open);
  };

  const handleChangeTextField = (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    setSearchKeyword(event.target.value);
  };

  const handleClickSelectButton = (
    newSelectedRecentLocation: RecentLocationContentByDeliveryDto
  ) => {
    setAddressForm(newSelectedRecentLocation.roadAddress);
    setWorkPlaceForm({
      name: newSelectedRecentLocation.workPlace.name,
      floor: newSelectedRecentLocation.workPlace.floor,
      phone: newSelectedRecentLocation.workPlace.phone,
      detail: newSelectedRecentLocation.workPlace.detail,
    });
    setOpen(false);
    setSearchKeyword("");
  };

  const doubounceSearchRecentLocation = useDebouncedCallback(async () => {
    if (!clientId) {
      toast.error("고객사를 선택해주세요.");
      setSearchKeyword("");
      return;
    }
    setSearchPayload();
  });

  useDidMountEffect(() => {
    doubounceSearchRecentLocation();
  }, [searchKeyword]);

  return (
    <RecentLocationSearchCardContainer>
      <LocationSearchField>
        <GeneralTextField
          placeholder="장소를 검색하세요"
          value={workPlaceForm.name}
          width="311px"
          isError={deliveryPayloadValidatorAddress}
          disabled
        />
        <OutlinedBlackButtonContainer>
          <OutlinedBlackButton
            width="5.438rem"
            height="2.625rem"
            text={"장소 검색"}
            onClick={handleClickLocationButton}
          />
        </OutlinedBlackButtonContainer>
      </LocationSearchField>

      <LocationContainer>
        {open ? (
          <RecentLocationSearchItemList
            searchKeyword={searchKeyword}
            recentLocations={recentLocations}
            handleChangeTextField={handleChangeTextField}
            handleClickSelectButton={handleClickSelectButton}
          />
        ) : (
          <SelectRecentLocationCard
            addressForm={addressForm}
            workPlaceForm={workPlaceForm}
            deliveryPayloadValidatorAddress={deliveryPayloadValidatorAddress}
            deliveryPayloadValidatorDetail={deliveryPayloadValidatorDetail}
            setAddressForm={setAddressForm}
            setWorkPlaceForm={setWorkPlaceForm}
          />
        )}
      </LocationContainer>
    </RecentLocationSearchCardContainer>
  );
};

const RecentLocationSearchCardContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: end;
`;

const LocationSearchField = styled.div`
  display: flex;
  justify-content: space-between;
  margin-bottom: 16px;
`;

const OutlinedBlackButtonContainer = styled.div`
  margin-left: 12px;
`;

const LocationContainer = styled.div``;
